import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { bookingApi } from '../services/api';
import { toast } from 'react-hot-toast';

interface TimeSlot {
  time: string; // ISO string
  displayTime: string; // HH:mm
}

interface TimeSlotSelectorProps {
  storeId: string;
  serviceId: string;
  selectedDate: string; // YYYY-MM-DD 格式
  selectedSlot: TimeSlot | null;
  onSlotSelect: (slot: TimeSlot) => void;
  staffId?: string;
}

const TimeSlotSelector: React.FC<TimeSlotSelectorProps> = ({
  storeId,
  serviceId,
  selectedDate,
  selectedSlot,
  onSlotSelect,
  staffId,
}) => {
  const { t } = useTranslation(['pages', 'translation']);
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [loading, setLoading] = useState(false);

  // 計算今天的日期字串（使用本地時區）
  const getTodayDateString = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // 當選擇日期或服務變更時，獲取可預約時段
  useEffect(() => {
    if (selectedDate && serviceId) {
      fetchAvailableSlots();
    }
  }, [selectedDate, serviceId, staffId]);

  const fetchAvailableSlots = async () => {
    if (!selectedDate || !serviceId) return;

    // ✅ 移除快取機制，每次都查詢最新資料（避免重複預約問題）
    try {
      setLoading(true);
      const response = await bookingApi.getAvailableSlots({
        storeId,
        serviceId,
        date: selectedDate,
        staffId,
      });

      // 轉換 API 返回的字串陣列為 TimeSlot 物件陣列
      let slots: TimeSlot[] = (response.data.availableSlots || []).map((timeStr: string) => ({
        time: `${selectedDate}T${timeStr}:00`, // ISO string
        displayTime: timeStr, // HH:mm
      }));

      // ✅ 如果選擇的是今天，過濾掉已經過去的時段
      const now = new Date();
      const isToday = selectedDate === getTodayDateString();

      if (isToday) {
        const currentHour = now.getHours();
        const currentMinute = now.getMinutes();

        slots = slots.filter((slot) => {
          const [slotHour, slotMinute] = slot.displayTime.split(':').map(Number);
          // 只保留未來的時段（至少30分鐘後）
          return (
            slotHour > currentHour ||
            (slotHour === currentHour && slotMinute > currentMinute + 30)
          );
        });
      }

      setAvailableSlots(slots);
    } catch (error: any) {
      console.error('Failed to fetch available slots:', error);
      setAvailableSlots([]);
      toast.error(t('pages:booking.dateTime.loadSlotsError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
        {t('pages:booking.dateTime.selectTime')}
        {loading && <span className="ml-2 text-xs text-gray-500">({t('pages:booking.dateTime.loading')})</span>}
      </label>

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
        </div>
      ) : availableSlots.length > 0 ? (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-64 overflow-y-auto">
          {availableSlots.map((slot) => (
            <button
              key={slot.time}
              type="button"
              onClick={() => onSlotSelect(slot)}
              className={`
                py-2 px-3 rounded-lg font-semibold text-sm transition-colors duration-200
                ${
                  selectedSlot?.time === slot.time
                    ? 'bg-brand-600 text-white ring-2 ring-brand-300 dark:ring-brand-700 shadow-md'
                    : 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-brand-100 dark:hover:bg-brand-900 hover:text-brand-600 dark:hover:text-brand-400'
                }
              `}
            >
              {slot.displayTime}
            </button>
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <div className="text-3xl mb-2">📅</div>
          <p className="text-gray-500 dark:text-gray-400 text-sm">
            {t('pages:booking.dateTime.noSlotsAvailable')}
          </p>
        </div>
      )}
    </div>
  );
};

export default TimeSlotSelector;
